import * as Dialog from '@radix-ui/react-dialog';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { BaseButtonProps } from '../button/BaseButton';
import { FormDialog } from './FormDialog';
import { InlineSelectField } from '../select/InlineSelectField';
import { IInlineSelectItem } from '../select/InlineSelect';
import { TextAreaField } from '../textarea/TextAreaField';

const schema = Yup.object().shape({
  content: Yup.string().required('Content is verplicht'),
  contacts: Yup.array().of(Yup.object()),
});

export interface IContactEntry {
  id: string;
  name: string;
  email: string;
}

export interface IContactsAndContentDialogProps {
  triggerText: React.ReactNode;
  triggerColor?: BaseButtonProps['color'];
  isDisabled?: boolean;
  title: string;
  description: string;
  initialContent: string;
  contacts: IContactEntry[];
  validation?: Yup.StringSchema;
  cancelText?: string;
  submitText: string;
  onSubmit: (value: { content: string; contacts: string[]; extra: Record<string, unknown> }) => void | Promise<void>;
  initialValue?: string[];
  /** Extra form fields rendered below the contacts; their values come back in `extra`. */
  extraFields?: React.ReactNode;
  extraInitialValues?: Record<string, unknown>;
}

interface ContactsAndContentDialogValues extends Record<string, unknown> {
  contacts: IInlineSelectItem[];
  content: string;
}

export const ContactsAndContentDialog: React.FC<IContactsAndContentDialogProps> = (props) => {
  const {
    onSubmit,
    initialValue,
    description,
    validation,
    initialContent,
    contacts,
    extraFields,
    extraInitialValues,
    ...otherProps
  } = props;

  const handleSubmit = async (values: ContactsAndContentDialogValues) => {
    const { contacts: selectedContacts, content, ...extra } = values;
    await onSubmit({
      contacts: selectedContacts.map((v) => v.key),
      content,
      extra,
    });
  };

  const items = useMemo(() => {
    return contacts.map((c) => {
      return {
        key: c.id,
        name: `${c.name} - ${c.email}`,
      };
    });
  }, [contacts]);

  const initialValues: ContactsAndContentDialogValues = useMemo(() => {
    return {
      ...extraInitialValues,
      content: initialContent,
      contacts: initialValue?.length
        ? initialValue
            .map((v) => {
              return items.find((c) => c.key === v)!;
            })
            .filter(Boolean)
        : [],
    };
  }, [initialValue, extraInitialValues]);

  return (
    <FormDialog {...otherProps} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
      <Dialog.Description asChild>
        <div className="mb-4">{description}</div>
      </Dialog.Description>

      <TextAreaField labelText="Bericht" name="content" />

      <InlineSelectField labelText="Contacten" name="contacts" items={items} selectAllLabel="Alle contacten" />

      {extraFields}
    </FormDialog>
  );
};
